import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent as NextUIPopoverContent,
} from '@nextui-org/react-latest'
import { EdugramIconNew } from '@public/static/icons/edugramIcon'

import HamburgerIcon from '@public/static/icons/hamburger'
import ArrowDown from '@public/static/icons/referrals/arrowDown'
import Link from 'next/link'
import { useCallback, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import styled from '@emotion/styled'
import { colors, fontFamilies } from '@configs/styles/config'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import languages from '@constants/languages'
import { useAuth } from '@contexts/Auth'
import dynamic from 'next/dynamic'

const WorldIcon = dynamic(
  () => import('@public/static/icons/footer/worldIcon'),
  { ssr: true }
)
const EnglishIcon = dynamic(
  () => import('@public/static/icons/languageIcons/english-icon'),
  { ssr: true }
)
const UkraineIcon = dynamic(
  () => import('@public/static/icons/languageIcons/ukraine-icon'),
  { ssr: true }
)
const LanguagesIcon = dynamic(
  () => import('@public/static/icons/languages-icon'),
  { ssr: true }
)
const SpanishIcon = dynamic(
  () => import('@public/static/icons/languages/spanishIcon'),
  { ssr: true }
)
const FrenchIcon = dynamic(
  () => import('@public/static/icons/languages/FrenchIcon'),
  { ssr: true }
)
const GermanIcon = dynamic(
  () => import('@public/static/icons/languages/GermanIcon'),
  { ssr: true }
)
const ItalianIcon = dynamic(
  () => import('@public/static/icons/languages/ItalianIcon'),
  { ssr: true }
)
const ReportProblem = dynamic(
  () => import('@components/organisms/ReportProblem'),
  { ssr: false }
)

type Props = {
  handleBecomeCreator: () => void
}

const HeaderContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 9;
  background-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(12px);
`

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: transparent;
  width: 100%;
  max-width: 1380px;
  margin: 0 auto;
  padding: 0 1rem;
  height: 83px;
`

const DesktopMenu = styled.div`
  display: none;
  align-items: center;
  height: 100%;
  /* gap: 0.25rem; */
  @media (min-width: 1200px) {
    /* gap: 1.5rem; */
  }
  @media (min-width: 1024px) {
    display: flex;
  }
`

const MenuItemWithArrow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  height: 100%;
  position: relative;
  padding: 0 0.8rem;
  @media (min-width: 1200px) {
    padding: 0 2rem;
    /* gap: 1.5rem; */
  }
  /* background-color: gray; */
  ::before {
    position: absolute;
    background-color: ${colors.uguLightGray};
    content: '';
    width: calc(100% - 1.5rem);
    height: 47px;
    bottom: 50%;
    transform: translateY(-50%);
    top: 50%;
    left: 0.75rem;
    z-index: 1;
    pointer-events: none;
    border-radius: 2rem;
    visibility: ${(props) => (props.isActive ? 'visible' : 'hidden')};
  }
  span {
    font-size: 16px;
    font-family: ${fontFamilies.medium};
    position: relative;
    z-index: 2;
  }
  svg {
    z-index: 2;
  }
`

const DesktopActions = styled.div`
  display: none;
  align-items: center;
  gap: 0.75rem;

  @media (min-width: 1024px) {
    display: flex;
  }
`

const MobileMenuButton = styled.div`
  display: block;

  @media (min-width: 1024px) {
    display: none;
  }
`

const MobileMenu = styled.div<{ isOpen: boolean }>`
  display: block;
  width: 100%;
  z-index: 999;
  position: absolute;
  left: 0;
  top: 100%;
  background-color: white;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  overflow: hidden;
  transition:
    max-height 0.35s ease-in-out,
    clip-path 0.35s ease-in-out;
  max-height: ${(props) => (props.isOpen ? 'calc(100dvh - 83px)' : '0px')};
  clip-path: ${(props) =>
    props.isOpen ? 'inset(0 0 0 0)' : 'inset(0 0 100% 0)'};
  padding-bottom: ${(props) => (props.isOpen ? '2rem' : '0px')};
  overflow-y: auto;
  border-bottom-left-radius: 2rem;
  border-bottom-right-radius: 2rem;
  @media (min-width: 1024px) {
    display: none;
  }
`

const MobileMenuContent = styled.div`
  padding: 1.5rem 1rem 0;
`

const MobileMenuItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const MobileActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
`

const FullWidthMenu = styled.div<{ isVisible: boolean }>`
  display: none;

  @media (min-width: 1024px) {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background: white;
    box-shadow: 0 10px 12px -1px rgba(0, 0, 0, 0.1);
    transform-origin: top;
    transform: ${(props) => (props.isVisible ? 'scaleY(1)' : 'scaleY(0)')};
    visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
    transition: transform 0.2s ease-in-out;
    z-index: 1000;
    display: flex;
  }
`

const MenuContent = styled.div`
  padding: 0.75rem 1rem 1rem 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  @media (min-width: 1024px) {
    padding: 0.75rem 1rem 2rem 0rem;
    max-width: 660px;
    margin: 0 0 0 ${(props) => `calc(${props.left}px + 2rem)`};
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 3rem;
  }
`

const MenuSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const SectionTitle = styled.h3`
  font-size: 14px;
  font-family: ${fontFamilies.medium};
  color: ${colors.uguDarkGrey};
  letter-spacing: 0.5px;
`

const SectionItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const MenuItem = styled(Link)`
  display: block;
  color: ${(props) =>
    props.isActive ? colors.uguBrightPurple : colors.uguPurple};
  font-size: 16px;
  text-decoration: none;
  transition: color 0.2s ease;
  font-family: ${fontFamilies.medium};

  &:hover {
    color: ${colors.uguBrightPurple};
  }
`

const MenuItemButton = styled.button`
  display: block;
  color: ${colors.uguPurple};
  font-size: 16px;
  text-decoration: none;
  transition: color 0.2s ease;
  font-family: ${fontFamilies.medium};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  &:hover {
    color: ${colors.uguBrightPurple};
  }
`
const LanguageItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-radius: 0.75rem;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0);
  border-color: ${colors.uguGray};
  &:hover {
    background-color: #f8fafc;
    border-color: ${colors.uguBrightPurple};
  }
  &.active {
    background-color: ${colors.uguLightBrightPurple};
    border-color: ${colors.uguBrightPurple};
  }
`

const FlagIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
`

const LanguageInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
`

const LanguageName = styled.span`
  font-size: 14px;
  font-family: ${fontFamilies.medium};
  color: ${colors.uguPurple};
  line-height: 1.2;
`

const PopoverContainer = styled.div`
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  min-width: 240px;
  flex-direction: column;
  gap: 1rem;
`

const PopoverHeader = styled.div`
  padding: 1rem 1rem 0.8rem 1rem;
  border-bottom: 1px solid ${colors.uguGray};
`

const PopoverTitle = styled.h3`
  font-size: 16px;
  font-family: ${fontFamilies.semiBold};
  color: ${colors.uguPurple};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const PopoverContent = styled.div`
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const CurrentLanguage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const SeoMenuStructure = styled.div`
  display: none;
`
export const supportedLanguages = [
  {
    code: 'en',
    name: languages.en.name,
    flagComponent: <EnglishIcon width="100%" height="100%" />,
  },
  {
    code: 'uk',
    name: languages.uk.name,
    flagComponent: <UkraineIcon width="100%" height="100%" />,
  },
  {
    code: 'es',
    name: languages.es.name,
    flagComponent: <SpanishIcon width="100%" height="100%" />,
  },
  {
    code: 'fr',
    name: languages.fr.name,
    flagComponent: <FrenchIcon width="100%" height="100%" />,
  },
  {
    code: 'de',
    name: languages.de.name,
    flagComponent: <GermanIcon width="100%" height="100%" />,
  },
  {
    code: 'it',
    name: languages.it.name,
    flagComponent: <ItalianIcon width="100%" height="100%" />,
  },
]

const Header = ({ handleBecomeCreator }: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuShown, setMenuShown] = useState('')
  const [isReportProblemOpen, setIsReportProblemOpen] = useState(false)
  const { user, isAuthLoading } = useAuth()
  const menuContainerRef = useRef<HTMLDivElement>(null)
  const desktopMenuRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  const handleMenuHover = (menuKey: string) => {
    setMenuShown(menuKey)
  }

  const getMenuPosition = () => {
    if (!desktopMenuRef.current) return { top: 0, left: 0, width: 0 }
    const { top, left, width } = desktopMenuRef.current.getBoundingClientRect()
    return { top, left, width }
  }

  const handleMenuLeave = (e: React.MouseEvent) => {
    // Only close if mouse is not moving to menu container or its children
    if (
      menuContainerRef.current &&
      e.relatedTarget instanceof Node &&
      menuContainerRef.current.contains(e.relatedTarget)
    ) {
      return
    }
    setMenuShown('')
  }

  const handleDropdownLeave = () => {
    setMenuShown('')
  }

  const handleReportProblem = () => {
    setIsReportProblemOpen(true)
    setIsMenuOpen(false)
  }

  const { i18n, t } = useTranslation('homeLanding/header')

  const menus = useMemo(
    () => [
      {
        key: 'solutions',
        sections: [
          {
            title: t('navigation.sections.onlineCourses'),
            items: [
              {
                title: t('navigation.items.createSellCourses'),
                href: '/solutions/online-courses',
              },
              {
                title: t('navigation.items.migrateCourses'),
                href: '/solutions/migrate-courses',
              },
              {
                title: t('navigation.items.createBundles'),
                href: '#',
              },
              {
                title: t('navigation.items.createPrograms'),
                href: '#',
              },
            ],
          },
          {
            title: t('navigation.sections.educationalContent'),
            items: [
              {
                title: t('navigation.items.createSubscriptions'),
                href: '#',
              },
              { title: t('navigation.items.monetizeFreeContent'), href: '#' },
              { title: t('navigation.items.growCommunity'), href: '#' },
            ],
          },
          {
            title: t('navigation.sections.earnGrow'),
            items: [
              { title: t('navigation.items.receiveThanks'), href: '#' },
              { title: t('navigation.items.inviteEarn'), href: '/referrals' },
              {
                title: t('navigation.items.audioTranslate'),
                href: '#',
              },
              {
                title: t('navigation.items.trackProgress'),
                href: '#',
              },
            ],
          },
        ],
      },
      {
        key: 'useCases',
        sections: [
          {
            title: t('navigation.sections.educators'),
            items: [
              {
                title: t('navigation.items.newCreators'),
                href: '/use-cases/new-creators/',
              },
              {
                title: t('navigation.items.establishedCreators'),
                href: '/use-cases/established-creators/',
              },
              {
                title: t('navigation.items.educationalVloggers'),
                href: '/use-cases/educational-vloggers/',
              },
              {
                title: t('navigation.items.teachersProfessors'),
                href: '/use-cases/teachers-professors/',
              },
              {
                title: t('navigation.items.mentors'),
                href: '/use-cases/mentors/',
              },
            ],
          },
          {
            title: t('navigation.sections.otherExperts'),
            items: [
              {
                title: t('navigation.items.vloggersBloggers'),
                href: '/use-cases/vloggers-bloggers/',
              },
              {
                title: t('navigation.items.streamers'),
                href: '/use-cases/streamers/',
              },
              {
                title: t('navigation.items.contentMakers'),
                href: '/use-cases/content-makers/',
              },
              {
                title: t('navigation.items.expertsField'),
                href: '/use-cases/experts-field/',
              },
              {
                title: t('navigation.items.schoolsUniversities'),
                href: '/use-cases/schools-universities/',
              },
            ],
          },
        ],
      },
      {
        key: 'resources',
        sections: [
          {
            title: t('navigation.sections.learn'),
            items: [
              {
                title: t('navigation.items.aboutUs'),
                href: '/resources/about-us',
              },
              { title: t('navigation.items.howToArticles'), href: '#' },
              { title: t('navigation.items.webinars'), href: '#' },
              {
                title: t('navigation.items.customerStories'),
                href: '#',
              },
            ],
          },
          {
            title: t('navigation.sections.support'),
            items: [
              {
                title: t('navigation.items.helpCentre'),
                href: '/help-centre/student',
              },
              { title: t('navigation.items.contactUs'), href: '/contact-us' },
              { title: t('navigation.items.reportProblem'), href: '#' },
            ],
          },
        ],
      },
    ],
    [t]
  )

  const currentMenu = useMemo(
    () => menus.find((menu) => menu.key === menuShown),
    [menuShown, menus]
  )

  const renderFullWidthMenu = useCallback(() => {
    return (
      <FullWidthMenu
        ref={menuContainerRef}
        isVisible={!!menuShown}
        onMouseLeave={handleDropdownLeave}
      >
        <MenuContent left={getMenuPosition().left}>
          {(currentMenu?.sections || []).map((section, index) => (
            <MenuSection key={index}>
              <SectionItems>
                <SectionTitle>{section.title}</SectionTitle>
                {section.items.map((item, itemIndex) =>
                  item.title.includes('Report') ? (
                    <MenuItemButton
                      key={itemIndex}
                      onClick={handleReportProblem}
                    >
                      {item.title}
                    </MenuItemButton>
                  ) : (
                    <MenuItem
                      key={itemIndex}
                      href={item.href}
                      isActive={
                        router.pathname !== '/' &&
                        item.href.includes(router.pathname)
                      }
                    >
                      {item.title}
                    </MenuItem>
                  )
                )}
              </SectionItems>
            </MenuSection>
          ))}
        </MenuContent>
      </FullWidthMenu>
    )
  }, [currentMenu, menuShown, router.pathname])

  const currentLanguage =
    supportedLanguages.find((lang) => lang.code === i18n.language) ||
    supportedLanguages[0]

  const handleLanguageChange = async (langCode: string) => {
    i18n.changeLanguage(langCode)
    router.push(router.asPath, router.asPath, { locale: langCode })
    localStorage.setItem('edugram-language', langCode)
  }

  const LanguageSelector = () => (
    <Popover placement="bottom-end" offset={10}>
      <PopoverTrigger>
        <Button
          radius="full"
          className="md:w-[47px] w-full h-[47px]"
          variant="flat"
          isIconOnly
          fullWidth
          aria-label={t('language.languageAriaLabel')}
        >
          <CurrentLanguage>
            <WorldIcon />
          </CurrentLanguage>
        </Button>
      </PopoverTrigger>
      <NextUIPopoverContent className="p-0">
        <PopoverContainer>
          <PopoverHeader>
            <PopoverTitle>
              <LanguagesIcon width="16" height="16" color={colors.uguPurple} />
              {t('language.selectLanguage')}
            </PopoverTitle>
          </PopoverHeader>
          <PopoverContent className="max-h-[24rem] overflow-auto">
            {supportedLanguages.map((language) => (
              <LanguageItem
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={
                  currentLanguage.code === language.code ? 'active' : ''
                }
              >
                <FlagIcon>{language.flagComponent}</FlagIcon>
                <LanguageInfo>
                  <LanguageName>{language.name}</LanguageName>
                </LanguageInfo>
              </LanguageItem>
            ))}
          </PopoverContent>
        </PopoverContainer>
      </NextUIPopoverContent>
    </Popover>
  )

  return (
    <>
      <HeaderContainer>
        <HeaderContent>
          <Link aria-label="Edugram Inc." href="/">
            <EdugramIconNew />
          </Link>

          {/* Desktop Menu */}
          <DesktopMenu ref={desktopMenuRef}>
            <MenuItemWithArrow
              onMouseEnter={() => handleMenuHover('solutions')}
              onMouseLeave={handleMenuLeave}
              isActive={menuShown === 'solutions'}
            >
              <span>{t('navigation.solutions')}</span>
              <ArrowDown />
            </MenuItemWithArrow>
            <MenuItemWithArrow
              onMouseEnter={() => handleMenuHover('useCases')}
              onMouseLeave={handleMenuLeave}
              isActive={menuShown === 'useCases'}
            >
              <span>{t('navigation.useCases')}</span>
              <ArrowDown />
            </MenuItemWithArrow>
            <MenuItemWithArrow
              onMouseEnter={() => handleMenuHover('resources')}
              onMouseLeave={handleMenuLeave}
              isActive={menuShown === 'resources'}
            >
              <span>{t('navigation.resources')}</span>
              <ArrowDown />
            </MenuItemWithArrow>
            <MenuItemWithArrow as={Link} href="/pricing">
              <span style={{ fontWeight: 500 }}>{t('navigation.pricing')}</span>
            </MenuItemWithArrow>
          </DesktopMenu>

          {/* Desktop Actions */}
          <DesktopActions>
            <Button
              radius="full"
              className="font-medium px-3.5 min-w-[150px] h-[47px]"
              size="md"
              color="primary"
              // as={Link}
              // href="/author/become-author"
              isLoading={isAuthLoading}
              onPress={handleBecomeCreator}
            >
              {user?.isAuthor
                ? t('actions.dashboard')
                : t('actions.becomeCreator')}
            </Button>
            <Button
              radius="full"
              className="font-medium px-3 min-w-[150px] h-[47px]"
              variant="flat"
              size="md"
              as={Link}
              href="/courses/"
            >
              {t('actions.goToApp')}
            </Button>
            <LanguageSelector />
          </DesktopActions>

          {/* Mobile Menu Button */}
          <MobileMenuButton>
            <Button
              // radius="full"
              variant="flat"
              size="lg"
              isIconOnly
              className="px-2.5"
              aria-label="Language"
              onPress={() => setIsMenuOpen(!isMenuOpen)}
            >
              <HamburgerIcon isOpen={isMenuOpen} />
            </Button>
          </MobileMenuButton>
        </HeaderContent>

        {/* Mobile Menu */}
        <MobileMenu isOpen={isMenuOpen}>
          <MobileMenuContent>
            <MobileMenuItems>
              {menus.map((menu) => (
                <div key={menu.key}>
                  <MenuItemWithArrow
                    onClick={(e) => {
                      if (menuShown === menu.key) {
                        handleMenuLeave(e)
                      } else if (menuShown) {
                        handleMenuLeave(e)
                        handleMenuHover(menu.key)
                      } else {
                        handleMenuHover(menu.key)
                      }
                    }}
                  >
                    <span>{t(`navigation.${menu.key}`)}</span>
                    <ArrowDown
                      className={`${menuShown === menu.key ? '-rotate-180' : ''} transition-all`}
                    />
                  </MenuItemWithArrow>
                  {currentMenu && currentMenu?.key === menu.key && (
                    <MenuContent>
                      {(currentMenu?.sections || []).map((section, index) => (
                        <MenuSection key={index}>
                          <SectionItems>
                            <SectionTitle>{section.title}</SectionTitle>
                            {section.items.map((item, itemIndex) =>
                              item.title.includes('Report') ? (
                                <MenuItemButton
                                  key={itemIndex}
                                  onClick={handleReportProblem}
                                >
                                  {item.title}
                                </MenuItemButton>
                              ) : (
                                <MenuItem
                                  key={itemIndex}
                                  href={item.href}
                                  isActive={
                                    router.pathname !== '/' &&
                                    item.href.includes(router.pathname)
                                  }
                                >
                                  {item.title}
                                </MenuItem>
                              )
                            )}
                          </SectionItems>
                        </MenuSection>
                      ))}
                    </MenuContent>
                  )}
                </div>
              ))}
              <MenuItemWithArrow>
                <Link href="/pricing">
                  <span>{t('navigation.pricing')}</span>
                </Link>
              </MenuItemWithArrow>

              <MobileActions>
                <Button
                  radius="full"
                  className="font-medium px-5 h-[47px]"
                  size="md"
                  color="primary"
                  fullWidth
                  onPress={handleBecomeCreator}
                >
                  {user?.isAuthor
                    ? t('actions.dashboard')
                    : t('actions.becomeCreator')}
                </Button>
                <Button
                  radius="full"
                  className="font-medium px-5 h-[47px]"
                  variant="flat"
                  size="md"
                  fullWidth
                  as={Link}
                  href="/courses/"
                >
                  {t('actions.goToApp')}
                </Button>
                <LanguageSelector />
              </MobileActions>
            </MobileMenuItems>
          </MobileMenuContent>
        </MobileMenu>
        {renderFullWidthMenu()}
      </HeaderContainer>
      {/* SEO: Hidden menu structure for search engines */}
      <SeoMenuStructure>
        <nav>
          {menus.map((menu) => (
            <div key={menu.key}>
              <h2>{t(`navigation.${menu.key}`)}</h2>
              {menu.sections.map((section, sectionIndex) => (
                <div key={sectionIndex}>
                  <h3>{section.title}</h3>
                  <ul>
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex}>
                        <Link href={item.href}>{item.title}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </nav>
      </SeoMenuStructure>
      <ReportProblem
        isOpen={isReportProblemOpen}
        onOpenChange={() => setIsReportProblemOpen(!isReportProblemOpen)}
        onClose={() => setIsReportProblemOpen(false)}
      />
    </>
  )
}

export default Header
